<script setup>
/**
 * Fork overlay (new file, no OSS equivalent) — EVA-M6.
 * Knowledge-source manager for one assistant: lists documents, adds a manual-text, crawl-URL or PDF
 * source, re-chunks (sync) or deletes one, via EvaDocumentsAPI. Tailwind-only; Composition API.
 */
import { reactive, ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EvaDocumentsAPI from 'dashboard/api/eva/documents';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistantId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const documents = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const hasError = ref(false);
const draft = reactive({
  name: '',
  mode: 'text',
  content: '',
  sourceUrl: '',
  file: null,
});

const statusLabels = computed(() => ({
  pending: t('EVA_DOCUMENTS.STATUS_PENDING'),
  available: t('EVA_DOCUMENTS.STATUS_AVAILABLE'),
  in_progress: t('EVA_DOCUMENTS.STATUS_IN_PROGRESS'),
}));

const statusClasses = {
  pending: 'bg-n-alpha-black2 text-n-slate-11',
  in_progress: 'bg-n-amber-3 text-n-amber-11',
  available: 'bg-n-teal-3 text-n-teal-11',
};

const modeLabels = computed(() => ({
  text: t('EVA_DOCUMENTS.MODE_TEXT'),
  url: t('EVA_DOCUMENTS.MODE_URL'),
  pdf: t('EVA_DOCUMENTS.MODE_PDF'),
}));

const canAdd = computed(() => {
  if (!draft.name.trim() || isSaving.value) return false;
  if (draft.mode === 'url') return draft.sourceUrl.trim() !== '';
  if (draft.mode === 'pdf') return draft.file !== null;
  return draft.content.trim() !== '';
});

const onFileChange = event => {
  draft.file = event.target.files[0] ?? null;
};

const load = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await EvaDocumentsAPI.getByAssistant(props.assistantId);
    documents.value = data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const add = async () => {
  if (!canAdd.value) return;
  isSaving.value = true;
  hasError.value = false;
  try {
    if (draft.mode === 'pdf') {
      // multipart: the PDF bytes go to the attachment; the model reads them inline for FAQ-gen.
      const form = new FormData();
      form.append('document[eva_assistant_id]', props.assistantId);
      form.append('document[name]', draft.name.trim());
      form.append('document[file]', draft.file);
      await EvaDocumentsAPI.create(form);
    } else {
      const payload = {
        eva_assistant_id: props.assistantId,
        name: draft.name.trim(),
      };
      if (draft.mode === 'url') payload.source_url = draft.sourceUrl.trim();
      else payload.content = draft.content;
      await EvaDocumentsAPI.create(payload);
    }
    Object.assign(draft, { name: '', content: '', sourceUrl: '', file: null });
    await load();
  } catch {
    hasError.value = true;
  } finally {
    isSaving.value = false;
  }
};

const remove = async id => {
  try {
    await EvaDocumentsAPI.delete(id);
  } catch {
    hasError.value = true;
  } finally {
    await load();
  }
};

const sync = async id => {
  try {
    await EvaDocumentsAPI.sync(id);
  } catch {
    hasError.value = true;
  } finally {
    await load();
  }
};

// --- inline edit (M7 7.2, B3) ---
const editingId = ref(null);
const editDraft = reactive({ name: '', content: '', isText: true });

const startEdit = async document => {
  // The list omits content (only `show` returns it). Fetch it for a text source so it can be edited;
  // a crawl/URL or PDF source is name-only (its content is crawler-/file-managed — not free text).
  // Populate the draft BEFORE opening the row, and don't open it if the content fetch fails (otherwise
  // the form would render with a previous edit's stale draft).
  const isText = !document.source_url && !document.is_pdf;
  hasError.value = false;
  try {
    let content = '';
    if (isText) {
      const { data } = await EvaDocumentsAPI.show(document.id);
      content = data.content ?? '';
    }
    Object.assign(editDraft, { name: document.name, content, isText });
    editingId.value = document.id;
  } catch {
    hasError.value = true;
  }
};

const cancelEdit = () => {
  editingId.value = null;
};

const saveEdit = async id => {
  isSaving.value = true;
  hasError.value = false;
  try {
    const payload = { name: editDraft.name.trim() };
    if (editDraft.isText) payload.content = editDraft.content;
    await EvaDocumentsAPI.update(id, payload);
    editingId.value = null;
    await load();
  } catch {
    hasError.value = true;
  } finally {
    isSaving.value = false;
  }
};

onMounted(load);

defineExpose({
  load,
  add,
  remove,
  sync,
  startEdit,
  cancelEdit,
  saveEdit,
  draft,
  editDraft,
});
</script>

<template>
  <div class="flex flex-col gap-4 min-w-0">
    <h3 class="text-sm font-medium text-n-slate-12">
      {{ t('EVA_DOCUMENTS.TITLE') }}
    </h3>

    <!-- add form -->
    <div
      class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
    >
      <Input
        v-model="draft.name"
        :label="t('EVA_DOCUMENTS.NAME')"
        :placeholder="t('EVA_DOCUMENTS.NAME_PLACEHOLDER')"
      />
      <div
        class="inline-flex self-start gap-0.5 p-0.5 rounded-lg bg-n-alpha-black2"
      >
        <button
          v-for="mode in ['text', 'url', 'pdf']"
          :key="mode"
          type="button"
          class="px-3 py-1 text-sm transition-colors rounded-md"
          :class="
            draft.mode === mode
              ? 'bg-n-solid-3 text-n-slate-12'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="draft.mode = mode"
        >
          {{ modeLabels[mode] }}
        </button>
      </div>
      <TextArea
        v-if="draft.mode === 'text'"
        v-model="draft.content"
        auto-height
        min-height="5rem"
        max-height="16rem"
        :placeholder="t('EVA_DOCUMENTS.CONTENT_PLACEHOLDER')"
      />
      <label
        v-else-if="draft.mode === 'pdf'"
        class="inline-flex items-center self-start gap-2 px-3 py-1.5 text-sm rounded-lg cursor-pointer outline outline-1 -outline-offset-1 outline-n-weak text-n-slate-12 hover:bg-n-alpha-black2"
      >
        <span class="i-lucide-paperclip size-4 text-n-slate-11" />
        {{ draft.file ? draft.file.name : t('EVA_DOCUMENTS.FILE_BUTTON') }}
        <input
          type="file"
          accept="application/pdf"
          class="hidden"
          @change="onFileChange"
        />
      </label>
      <Input
        v-else
        v-model="draft.sourceUrl"
        :placeholder="t('EVA_DOCUMENTS.URL_PLACEHOLDER')"
      />
      <div class="flex justify-end">
        <Button
          :label="t('EVA_DOCUMENTS.ADD')"
          sm
          :is-loading="isSaving"
          :disabled="!canAdd"
          @click="add"
        />
      </div>
    </div>

    <p v-if="hasError" class="text-sm text-n-ruby-11">
      {{ t('EVA_DOCUMENTS.ERROR') }}
    </p>
    <p v-else-if="isLoading" class="text-sm text-n-slate-11">
      {{ t('EVA_DOCUMENTS.LOADING') }}
    </p>
    <p
      v-else-if="!documents.length"
      class="px-6 py-6 text-sm text-center border border-dashed rounded-xl border-n-weak text-n-slate-11"
    >
      {{ t('EVA_DOCUMENTS.EMPTY') }}
    </p>

    <ul
      v-else
      class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container divide-y divide-n-weak"
    >
      <li
        v-for="document in documents"
        :key="document.id"
        class="flex flex-col gap-3 p-4 min-w-0"
      >
        <!-- edit form -->
        <template v-if="editingId === document.id">
          <Input v-model="editDraft.name" :label="t('EVA_DOCUMENTS.NAME')" />
          <TextArea
            v-if="editDraft.isText"
            v-model="editDraft.content"
            auto-height
            min-height="6rem"
            max-height="20rem"
            :message="t('EVA_DOCUMENTS.EDIT_RECHUNK_HINT')"
          />
          <div class="flex justify-end gap-2">
            <Button
              faded
              slate
              sm
              :label="t('EVA_DOCUMENTS.CANCEL')"
              @click="cancelEdit"
            />
            <Button
              sm
              :label="t('EVA_DOCUMENTS.SAVE')"
              :is-loading="isSaving"
              :disabled="isSaving || !editDraft.name.trim()"
              @click="saveEdit(document.id)"
            />
          </div>
        </template>

        <!-- static row -->
        <div v-else class="flex items-center justify-between gap-3">
          <div class="flex flex-col gap-1 min-w-0">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ document.name }}
            </span>
            <span
              class="flex flex-wrap items-center gap-2 text-xs text-n-slate-11"
            >
              <span
                class="inline-flex px-1.5 py-0.5 rounded-md"
                :class="statusClasses[document.status]"
              >
                {{ statusLabels[document.status] }}
              </span>
              <span
                v-if="document.is_pdf"
                class="inline-flex px-1.5 py-0.5 rounded-md bg-n-alpha-black2"
              >
                {{ t('EVA_DOCUMENTS.PDF') }}
              </span>
              <span>{{
                t('EVA_DOCUMENTS.SEARCHABLE', {
                  searchable: document.searchable_count ?? 0,
                  total: document.responses_count,
                })
              }}</span>
            </span>
            <span
              v-if="
                document.responses_count > 0 &&
                (document.searchable_count ?? 0) === 0
              "
              class="text-xs text-n-amber-11"
            >
              {{ t('EVA_DOCUMENTS.WORKER_WARNING') }}
            </span>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <Button
              slate
              xs
              ghost
              :label="t('EVA_DOCUMENTS.SYNC')"
              @click="sync(document.id)"
            />
            <span class="w-px h-4 bg-n-weak" />
            <Button
              icon="i-lucide-pen"
              slate
              xs
              ghost
              :aria-label="t('EVA_DOCUMENTS.EDIT')"
              @click="startEdit(document)"
            />
            <Button
              icon="i-lucide-trash"
              slate
              xs
              ghost
              :aria-label="t('EVA_DOCUMENTS.DELETE')"
              @click="remove(document.id)"
            />
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
